import React, { useState, useMemo, useRef, useEffect, useContext } from "react";
import {
    printReports,
    getSignalCardClass,
    getProgressWidthClass,
    getSignalColorFromDevice,
} from "../../utils";
import { useProductionData } from "../../hooks/useProductionData";
import StarekContext from "../../context/StarekContext";
import ReportResults from "./ReportResults";

export default function LiveProductionMonitor({
    filters,
    range,
    deviceData,
    onRefresh,
    onFavoriteClick,
    onDownloadCSV,
    showAssignJobLink = true,
    hideControls = false,
    cachedDatapoints,
    isLoadingDatapoints,
    selectedDeviceIndex,
    onSelectedDeviceIndexChange,
}) {
    const [loading, setLoading] = useState(false);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [hideEmptyDevices, setHideEmptyDevices] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState(null);
    const printRef = useRef(null);
    const starekContext = useContext(StarekContext);
    const { auth, lastUsedWorkspace } = starekContext;
    const types = starekContext.types;

    // Calculate time range for production data
    const timeRange = useMemo(() => {
        console.log("LiveProductionMonitor - filters:", filters);
        console.log(
            "LiveProductionMonitor - durationStart:",
            filters?.durationStart,
        );
        console.log(
            "LiveProductionMonitor - durationEnd:",
            filters?.durationEnd,
        );

        if (!filters?.durationStart || !filters?.durationEnd) {
            console.log(
                "LiveProductionMonitor - timeRange is NULL (missing duration)",
            );
            return null;
        }

        let start = filters.durationStart;
        let end = filters.durationEnd;

        if (Array.isArray(range) && range.length === 2) {
            const base = new Date(filters.durationStart);
            if (!Number.isNaN(base.getTime())) {
                const [startOffsetSec, endOffsetSec] = range;
                start = new Date(
                    base.getTime() + startOffsetSec * 1000,
                ).toISOString();
                end = new Date(
                    base.getTime() + endOffsetSec * 1000,
                ).toISOString();
            }
        }

        const timeRange = { start, end };
        console.log("LiveProductionMonitor - timeRange created:", timeRange);
        return timeRange;
    }, [filters, range]);

    // Filter devices based on selection
    const allShadowDevices = deviceData?.shadowDevices || [];
    const shadowDevices = useMemo(() => {
        if (!allShadowDevices || allShadowDevices.length === 0) return [];
        if (!filters?.devices) return allShadowDevices;

        const selectedDeviceNames = filters.devices
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);

        if (selectedDeviceNames.length === 0) return allShadowDevices;

        return allShadowDevices.filter((dev) => {
            const deviceName = dev.name || dev.id;
            return selectedDeviceNames.includes(deviceName);
        });
    }, [allShadowDevices, filters?.devices]);

    console.log(
        "LiveProductionMonitor - shadowDevices count:",
        shadowDevices.length,
    );
    console.log("LiveProductionMonitor - timeRange passed to hook:", timeRange);

    const {
        data: productionDevices,
        loading: productionLoading,
        refetch,
    } = useProductionData(
        shadowDevices,
        timeRange,
        cachedDatapoints,
        isLoadingDatapoints,
    );

    const handleDownloadCSV = () => {
        if (onDownloadCSV) onDownloadCSV();
    };

    const handleRefresh = async () => {
        setLoading(true);
        try {
            // Refetch both the device data from parent and production data
            await Promise.all([onRefresh?.(), refetch()]);
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyzeWithAI = async () => {
        try {
            setAiAnalyzing(true);
            setAiError(null);
            setShowAiPanel(true);

            const { scheme, host, port } = lastUsedWorkspace.engine;
            const workspaceId = lastUsedWorkspace.name;

            const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints/ai-summary`;

            const payload = {
                input: "Analyze the device production data and provide insights on trends, anomalies, and recommendations",
                data: productionDevices,
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("AI Analysis Result:", result);
            setAiResults(result);
        } catch (error) {
            console.error("Error analyzing with AI:", error);
            setAiError("Failed to analyze data with AI. Please try again.");
            setAiResults(null);
        } finally {
            setAiAnalyzing(false);
        }
    };

    useEffect(() => {
        setLoading(productionLoading);
    }, [productionLoading]);

    // Setup auto-refresh interval
    useEffect(() => {
        if (autoRefresh && refetch) {
            const intervalId = setInterval(() => {
                refetch().catch((err) =>
                    console.error("Auto-refresh failed:", err),
                );
            }, 60000); // 60 seconds
            setAutoRefreshIntervalId(intervalId);

            return () => {
                if (intervalId) clearInterval(intervalId);
            };
        } else {
            // Clear interval if auto-refresh is disabled
            if (autoRefreshIntervalId) {
                clearInterval(autoRefreshIntervalId);
                setAutoRefreshIntervalId(null);
            }
        }
    }, [autoRefresh, refetch]);

    const devices = useMemo(() => {
        try {
            if (!productionDevices || productionDevices.length === 0) return [];

            return productionDevices.map((dev) => {
                // Parse device parameters to get signal info
                let deviceParams = {};
                if (dev.parameters) {
                    try {
                        deviceParams =
                            typeof dev.parameters === "string"
                                ? JSON.parse(dev.parameters)
                                : dev.parameters;
                    } catch (e) {
                        console.error("Failed to parse device parameters", e);
                    }
                }

                const displayName = dev.name || dev.device?.name || dev.id;

                // Get total transitions (calculated by useProductionData hook)
                const allTransitions = [];
                const totalUnitsProduced = dev.totalUnitsProduced || 0;

                console.log("Device production data for", displayName, ":", {
                    productionCalculated: dev.productionCalculated,
                    jobSchedules: dev.jobSchedules?.length || 0,
                    totalUnitsProduced: totalUnitsProduced,
                });

                // Calculate time range in milliseconds for job filtering
                const filterStartMs = timeRange
                    ? new Date(timeRange.start).getTime()
                    : null;
                const filterEndMs = timeRange
                    ? new Date(timeRange.end).getTime()
                    : null;

                const activeJob = (dev.jobSchedules || [])
                    .filter((job) => {
                        if (!filterStartMs || !filterEndMs) return true;
                        if (!job.start_time || !job.end_time) return false;
                        const jobStartMs = new Date(job.start_time).getTime();
                        const jobEndMs = new Date(job.end_time).getTime();
                        if (isNaN(jobStartMs) || isNaN(jobEndMs)) return false;
                        return (
                            jobStartMs < filterEndMs && jobEndMs > filterStartMs
                        );
                    })
                    .sort((a, b) => {
                        const aStart = new Date(a.start_time).getTime();
                        const bStart = new Date(b.start_time).getTime();
                        return bStart - aStart;
                    })[0];
                const activePartName =
                    activeJob?.part?.name || activeJob?.part_name || null;
                console.log("Active part for", displayName, ":", {
                    partName: activePartName,
                    jobId: activeJob?.id,
                    jobName: activeJob?.name,
                    windowStart: timeRange?.start,
                    windowEnd: timeRange?.end,
                });

                const hasDatapoints = dev.productionCalculated === true;
                console.log("hasDatapoints check for", displayName, ":", {
                    productionCalculated: dev.productionCalculated,
                    hasDatapoints,
                    totalUnitsProduced: totalUnitsProduced,
                    shouldShowData: totalUnitsProduced > 0,
                });

                // Get runtime signals from device - use them dynamically instead of hardcoding
                const runtimeSignals = dev.device?.runtime?.signals || [];
                const signalsToProcess = Array.isArray(runtimeSignals)
                    ? runtimeSignals.filter((s) => s != null)
                    : [];

                const schedules = signalsToProcess.map(
                    (signal, signalIndex) => {
                        // Use the totalUnitsProduced calculated by the hook
                        const unitsProduced = hasDatapoints
                            ? totalUnitsProduced
                            : 0;

                        // TODO: Target should be calculated from heuristics
                        // For now, using a simple estimation based on job duration and typical production rate
                        const targetUnits = hasDatapoints
                            ? Math.max(
                                  unitsProduced + 50,
                                  Math.floor(unitsProduced * 1.2),
                              )
                            : 0;

                        const progress =
                            hasDatapoints && targetUnits > 0
                                ? Math.min(
                                      (unitsProduced / targetUnits) * 100,
                                      100,
                                  )
                                : 0;

                        // Get signal details from parameters using the signal name
                        const signalName = signal.name;
                        const signalParam = deviceParams[signalName] || {};
                        const defaultSignalColors =
                            types?.device?.defaultSignalColors || [];

                        const resolvedColor = getSignalColorFromDevice(
                            dev,
                            signalName,
                            signalIndex,
                            defaultSignalColors,
                        );

                        return {
                            id: `${dev.id}-${signalName}`,
                            label: signalName,
                            signalType: signal.type,
                            signalName: signalParam.name || signal.name,
                            signalColor: resolvedColor,
                            unitsProduced: unitsProduced,
                            targetUnits: targetUnits,
                            progress: progress,
                            transitions: allTransitions,
                            hasDatapoints: hasDatapoints,
                            partName: activePartName || "Unassigned Part",
                            type: "success",
                            deviceName: displayName,
                        };
                    },
                );

                // Always use the main schedules (mapped from SIGNALS_TO_PLOT)
                const allSchedules = schedules;

                return {
                    id: dev.id,
                    name: displayName,
                    schedules: allSchedules,
                    hasDatapoints,
                    parameters: deviceParams,
                };
            });
        } catch (error) {
            console.error("Error in devices useMemo:", error);
            return [];
        }
    }, [productionDevices, filters]);

    const visibleDevices = useMemo(() => {
        if (!hideEmptyDevices) return devices;
        return devices.filter(
            (device) => device.hasSignals && device.hasDatapoints,
        );
    }, [devices, hideEmptyDevices]);

    const filteredDevices = useMemo(() => {
        if (visibleDevices.length === 0) return [];
        // Use validated index instead of raw selectedDeviceIndex
        const validIndex = Math.min(
            selectedDeviceIndex,
            visibleDevices.length - 1,
        );
        const selectedDevice = visibleDevices[Math.max(0, validIndex)];
        return selectedDevice ? [selectedDevice] : [];
    }, [visibleDevices, selectedDeviceIndex]);

    const handlePrevDevice = () => {
        onSelectedDeviceIndexChange((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handleNextDevice = () => {
        onSelectedDeviceIndexChange((prev) =>
            prev < navigableDevices.length - 1 ? prev + 1 : prev,
        );
    };

    const navigableDevices = useMemo(() => {
        // Use visibleDevices for navigation (has proper .name property)
        return visibleDevices;
    }, [visibleDevices]);

    return (
        <ReportResults
            deviceList={navigableDevices}
            selectedDeviceIndex={Math.min(
                selectedDeviceIndex,
                Math.max(0, navigableDevices.length - 1),
            )}
            onPrevDevice={handlePrevDevice}
            onNextDevice={handleNextDevice}
            hideEmptyDevices={hideEmptyDevices}
            onHideEmptyDevicesChange={(checked) => {
                setHideEmptyDevices(checked);
                onSelectedDeviceIndexChange(0);
            }}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            loading={loading}
            onRefresh={handleRefresh}
            onFavoriteClick={onFavoriteClick}
            onPrintClick={() => printReports(printRef)}
            onDownloadCSV={handleDownloadCSV}
            printRef={printRef}
            isAIAnalysisOpen={showAiPanel}
            isAIAnalyzing={aiAnalyzing}
            aiResults={aiResults}
            aiError={aiError}
            onCloseAIAnalysis={() => {
                setShowAiPanel(false);
                setAiResults(null);
                setAiError(null);
            }}
            onAIFollowUp={(question) => {
                console.log("Follow-up question:", question);
                // Follow-up logic can be implemented here
            }}
        >
            {/* Main Content: Grid of Devices and Signals */}
            {filteredDevices.length > 0 && filteredDevices[0]?.schedules ? (
                <div className="tw-flex tw-flex-col tw-gap-4">
                    {filteredDevices.map((device) => {
                        const totalJobs = device.schedules?.length || 0;
                        return (
                            <div
                                key={device.id}
                                className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-shadow-sm tw-p-4"
                            >
                                {/* Device Header */}
                                <div className="tw-mb-4 tw-pb-3 tw-border-b tw-border-slate-200">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <i className="fas fa-microchip tw-text-slate-500"></i>
                                        <h3 className="tw-text-sm tw-font-bold tw-text-slate-900">
                                            {device.name}
                                        </h3>
                                        <span className="tw-ml-auto tw-text-xs tw-text-slate-500">
                                            {totalJobs}{" "}
                                            {totalJobs === 1
                                                ? "signal"
                                                : "signals"}
                                        </span>
                                    </div>
                                </div>

                                {/* Signals Grid */}
                                {(device.schedules?.length || 0) === 0 ? (
                                    <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-py-6 tw-text-xs tw-font-semibold tw-text-slate-500">
                                        No Reported Signals
                                    </div>
                                ) : (
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                                        {(device.schedules || []).map((sch) => {
                                            const unitsProduced =
                                                sch.unitsProduced || 0;
                                            const targetUnits =
                                                sch.targetUnits || 0;
                                            const hasDatapoints =
                                                sch.hasDatapoints ?? true;
                                            const signalName =
                                                sch.signalName || sch.label;
                                            const signalColor =
                                                sch.signalColor || "#3b82f6";
                                            const signalType =
                                                sch.signalType || "unknown";
                                            const cardClass =
                                                getSignalCardClass(signalColor);
                                            const partName =
                                                sch.partName ||
                                                sch.part?.name ||
                                                "Unassigned Part";
                                            const isUnassignedPart =
                                                partName === "Unassigned Part";
                                            const progressPercent =
                                                hasDatapoints && targetUnits > 0
                                                    ? Math.min(
                                                          (unitsProduced /
                                                              targetUnits) *
                                                              100,
                                                          100,
                                                      )
                                                    : 0;
                                            const unitsProducedDisplay =
                                                hasDatapoints
                                                    ? unitsProduced.toLocaleString()
                                                    : "--";
                                            const targetUnitsDisplay =
                                                hasDatapoints
                                                    ? targetUnits.toLocaleString()
                                                    : "--";
                                            const progressTextClass =
                                                progressPercent < 33
                                                    ? "tw-text-red-600"
                                                    : progressPercent <= 66
                                                      ? "tw-text-yellow-600"
                                                      : "tw-text-green-600";
                                            const progressBarClass =
                                                progressPercent < 33
                                                    ? "tw-bg-red-500"
                                                    : progressPercent <= 66
                                                      ? "tw-bg-yellow-500"
                                                      : "tw-bg-green-500";
                                            const progressWidthClass =
                                                getProgressWidthClass(
                                                    progressPercent,
                                                );

                                            return (
                                                <div
                                                    key={sch.id}
                                                    className={`tw-p-2.5 tw-rounded-lg tw-border tw-shadow-sm hover:tw-shadow-md tw-transition-shadow ${cardClass}`}
                                                >
                                                    {/* Signal Name */}
                                                    <div className="tw-flex tw-items-center tw-gap-1 tw-mb-2 tw-text-xs tw-text-slate-700">
                                                        <i
                                                            className={`fas ${signalType === "digital_input" ? "fa-toggle-on" : "fa-chart-line"}`}
                                                        ></i>
                                                        <span
                                                            className="tw-truncate tw-font-semibold"
                                                            title={signalName}
                                                        >
                                                            {signalName}
                                                        </span>
                                                    </div>

                                                    {/* Units Progress */}
                                                    <div className="tw-mb-2">
                                                        <div className="tw-flex tw-items-baseline tw-justify-between tw-mb-1">
                                                            <span
                                                                className={`tw-text-lg tw-font-bold ${progressTextClass}`}
                                                            >
                                                                {
                                                                    unitsProducedDisplay
                                                                }
                                                            </span>
                                                            <span className="tw-text-xs tw-text-slate-400">
                                                                /{" "}
                                                                {
                                                                    targetUnitsDisplay
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="tw-w-full tw-bg-slate-200 tw-rounded-full tw-h-2 tw-overflow-hidden">
                                                            <div
                                                                className={`tw-h-2 tw-rounded-full tw-transition-all tw-duration-500 ${progressBarClass} ${progressWidthClass}`}
                                                            ></div>
                                                        </div>
                                                    </div>

                                                    {isUnassignedPart ? (
                                                        <div className="tw-flex tw-items-center tw-gap-2">
                                                            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-red-200 tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-red-700">
                                                                UNASSIGNED JOB
                                                            </span>
                                                            {showAssignJobLink && (
                                                                <a
                                                                    className="tw-text-[10px] tw-font-semibold tw-text-red-700 hover:tw-underline"
                                                                    href={`/job-schedules?deviceId=${encodeURIComponent(device.id)}`}
                                                                >
                                                                    Assign Job
                                                                </a>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="tw-text-xs tw-text-slate-500">
                                                            {partName}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                    <span className="tw-text-slate-400">No devices found.</span>
                </div>
            )}
        </ReportResults>
    );
}
